import { useCallback, useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { Button } from './ui/Button';

export interface MnemonicVerifyProps {
  /** 待验证的 12 词（顺序即正确答案） */
  words: string[];
  /** 全部点选正确后回调（**强阻断**：未回调不得放行到主界面） */
  onVerified: () => void;
  className?: string;
}

function shuffle<T>(list: T[]): T[] {
  const out = [...list];
  for (let i = out.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

/**
 * 乱序点选逐词验证（修复 PRD §9.3 的备份缺失风险）。
 *
 * 规则：
 *  - 12 个词打乱后平铺，用户须**按原顺序**逐个点选
 *  - 点错立即提示并清空已选进度，必须重来
 *  - 全部点完后才触发 `onVerified`
 */
export function MnemonicVerify({ words, onVerified, className }: MnemonicVerifyProps): JSX.Element {
  const [pool, setPool] = useState<Array<{ word: string; uid: string }>>([]);
  const [picked, setPicked] = useState<string[]>([]);
  const [errorIndex, setErrorIndex] = useState<number | null>(null);

  useEffect(() => {
    setPool(shuffle(words.map((word, i) => ({ word, uid: `${word}#${i}` }))));
    setPicked([]);
    setErrorIndex(null);
  }, [words]);

  const expectedIndex = picked.length;

  const handlePick = useCallback(
    (uid: string, word: string) => {
      if (word !== words[expectedIndex]) {
        setErrorIndex(expectedIndex);
        setPicked([]);
        return;
      }
      setErrorIndex(null);
      const next = [...picked, uid];
      setPicked(next);
      if (next.length === words.length) onVerified();
    },
    [expectedIndex, onVerified, picked, words],
  );

  const progress = useMemo(() => `${picked.length}/${words.length}`, [picked.length, words.length]);

  return (
    <div className={clsx('space-y-4', className)}>
      <div className="flex items-center justify-between text-xs text-dim">
        <span>请按顺序点选助记词</span>
        <span className="mono">{progress}</span>
      </div>

      {/* 已选区 */}
      <div className="min-h-[72px] rounded-card border border-line bg-panel-2 p-3">
        <div className="flex flex-wrap gap-2">
          {picked.map((uid, i) => (
            <span key={uid} className="mono rounded-btn bg-accent-dim px-2 py-1 text-xs text-accent">
              {i + 1}. {uid.split('#')[0]}
            </span>
          ))}
          {picked.length === 0 ? <span className="text-xs text-dim">尚未选择</span> : null}
        </div>
      </div>

      {/* 候选池 */}
      <div className="grid grid-cols-3 gap-2">
        {pool.map(({ word, uid }) => {
          const used = picked.includes(uid);
          return (
            <button
              key={uid}
              type="button"
              disabled={used}
              onClick={() => handlePick(uid, word)}
              className={clsx(
                'mono rounded-btn border px-2 py-2 text-sm transition-colors',
                used
                  ? 'border-line bg-panel text-dim opacity-40'
                  : 'border-line bg-panel-2 text-text hover:border-accent',
              )}
            >
              {word}
            </button>
          );
        })}
      </div>

      {errorIndex !== null ? (
        <div className="rounded-btn border border-err/40 bg-err/10 px-3 py-2 text-xs text-err">
          第 {errorIndex + 1} 个词不对，已清空进度，请重新按顺序点选。
        </div>
      ) : null}

      <Button variant="ghost" size="sm" onClick={() => setPool(shuffle(pool))}>
        重新打乱
      </Button>
    </div>
  );
}

export default MnemonicVerify;
